import React, { useEffect, useRef } from 'react';

interface WhiteListItem {
  elementValue?: string;
  attr?: string;
  attrValue?: string;
  type?: string;
  value?: string;
}

interface ClickOutSideProps {
  children: React.ReactNode;
  onClickOutSide: (event: MouseEvent) => void;
  whiteList?: WhiteListItem[];
  className?: string;
}

const ClickOutSide = ({
  children,
  onClickOutSide,
  whiteList = [],
  className,
}: ClickOutSideProps) =>
{
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() =>
  {
    const handleClickOutside = (event: MouseEvent) =>
    {
      // If the target was detached from the DOM by a React re-render before this
      // mousedown event finished bubbling (e.g. react-select option nodes replaced
      // synchronously by a state update), treat it as an inside click and ignore.
      if (!document.body.contains(event.target as Node)) return;

      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node))
      {
        // Check for date picker popover
        const datePickerPopover = document.getElementById('date-picker-popover-0');
        if (datePickerPopover && datePickerPopover.contains(event.target as Node))
        {
          return;
        }

        // Check whitelist
        if (whiteList && whiteList.length > 0)
        {
          const target = event.target as Element;
          const matches = whiteList.filter(item =>
          {
            if (item.elementValue)
            {
              return target.textContent === item.elementValue;
            }

            if (item.attr && item.attrValue)
            {
              return target.getAttribute(item.attr) === item.attrValue;
            }

            // Check by type (class or other selector)
            if (item.type === 'class')
            {
              return target.classList.contains(item.value ?? '');
            }

            // Check by tag name or id
            if (item.type === 'id')
            {
              return (target as HTMLElement).id === item.value;
            }

            return (target as any).matches?.(item.value) || false;
          });

          if (matches.length > 0)
          {
            return;
          }
        }

        onClickOutSide(event);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () =>
    {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClickOutSide, whiteList]);

  return (
    <div ref={wrapperRef} className={className}>
      {children}
    </div>
  );
};

export default ClickOutSide;
